import CloseIcon from "@/assets/images/icons/x-circle.svg";
import { HomeSurveyInstrumentIcon } from "@/components/courses/HomeSurveyInstrumentIcon";
import { useErrorHandler } from "@/components/ui/ErrorHandlerProvider";
import { useAuth } from "@/lib/AuthContext";
import { BRAND_COLORS } from "@/lib/brandColors";
import { dashboardToolbarIconColor } from "@/lib/dashboardToolbarUi";
import {
  HOME_SURVEY_INSTRUMENTS,
  HOME_SURVEY_LEVELS,
  type HomeSurveyInstrumentId,
  type HomeSurveyLevelId,
} from "@/lib/homeSurveyOptions";
import { readHomeSurveyPrefillFromMetadata } from "@/lib/dashboardHomeSurvey";
import { LOADING_SPINNER_COLOR } from "@/lib/loadingSpinnerColor";
import type { UserPreferenceMetadata } from "@/lib/userPreferences";
import {
  setHomeDashboard,
  setHomeFailureMessage,
} from "@/services/set-home-dashboard.service";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  useWindowDimensions,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Button, Dialog, VisuallyHidden, XStack, YStack, useMedia } from "tamagui";
import { BodyText, CaptionText, ContentTitle, SectionTitle, SubtitleText } from "@/components/ui/Typography";

type Step = "instrument" | "level";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  /** Renders the survey inline (no dialog overlay) — for post-purchase onboarding. */
  embedded?: boolean;
  /** Visual polish for the post-purchase onboarding card. */
  tone?: "default" | "onboarding";
  /** Overrides the instrument-step header when embedded (e.g. post-purchase welcome). */
  welcomeHeader?: { title: string; subtitle: string };
  /** Shown instead of the close button when embedded. */
  onSkip?: () => void;
};

function instrumentRows(twoCol: boolean) {
  if (!twoCol) {
    return HOME_SURVEY_INSTRUMENTS.map((item) => [item]);
  }
  const rows: (typeof HOME_SURVEY_INSTRUMENTS)[] = [];
  for (let i = 0; i < HOME_SURVEY_INSTRUMENTS.length; i += 2) {
    rows.push(HOME_SURVEY_INSTRUMENTS.slice(i, i + 2));
  }
  return rows;
}

const InstrumentOption = React.memo(function InstrumentOption({
  item,
  selected,
  onPick,
}: {
  item: (typeof HOME_SURVEY_INSTRUMENTS)[number];
  selected?: boolean;
  onPick: (id: HomeSurveyInstrumentId) => void;
}) {
  return (
    <YStack
      w="100%"
      br={10}
      borderWidth={1}
      borderColor={selected ? BRAND_COLORS.text : "rgba(0,0,0,0.08)"}
      bg={selected ? "#F4F1ED" : BRAND_COLORS.cardBg}
      px="$4"
      py="$3"
      hoverStyle={{ borderColor: BRAND_COLORS.text }}
      pressStyle={{ opacity: 0.92, scale: 0.99 }}
      onPress={() => onPick(item.id)}
      accessibilityRole="button"
      accessibilityLabel={item.label}
    >
      <XStack ai="center" gap={10} minWidth={0}>
        <HomeSurveyInstrumentIcon
          id={item.id}
          size={22}
          color={dashboardToolbarIconColor(!!selected)}
        />
        <SubtitleText color={BRAND_COLORS.text} flexShrink={1}>
          {item.label}
        </SubtitleText>
      </XStack>
    </YStack>
  );
});

export function FindDashboardSurveyModal({
  open,
  onOpenChange,
  onSuccess,
  embedded = false,
  tone = "default",
  welcomeHeader,
  onSkip,
}: Props) {
  const { showError } = useErrorHandler();
  const { user, preferenceMetadata, refreshUserPreferences, applyPreferenceMetadata } = useAuth();
  const media = useMedia();
  const insets = useSafeAreaInsets();
  const { width, height: screenHeight } = useWindowDimensions();
  const isWeb = Platform.OS === "web";

  const [step, setStep] = useState<Step>("instrument");
  const [instrument, setInstrument] = useState<HomeSurveyInstrumentId | null>(null);
  const [busy, setBusy] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const prefill = useMemo(() => {
    const { instrument: inst, level } = readHomeSurveyPrefillFromMetadata(preferenceMetadata);
    const instrumentId = HOME_SURVEY_INSTRUMENTS.some((item) => item.id === inst)
      ? (inst as HomeSurveyInstrumentId)
      : null;
    const levelId =
      level === "beginner" || level === "intermediate" || level === "advanced"
        ? level
        : null;
    return { instrument: instrumentId, level: levelId };
  }, [preferenceMetadata]);

  const reset = useCallback(() => {
    setStep("instrument");
    setInstrument(null);
    setBusy(false);
    setIsUpdateMode(false);
  }, []);

  const applyPrefill = useCallback(() => {
    setStep("instrument");
    setInstrument(null);
    setBusy(false);
    setIsUpdateMode(!!prefill.instrument);
  }, [prefill.instrument]);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  useEffect(() => {
    if (!open) return;
    applyPrefill();
    // Only initialize when the modal opens — not when preference metadata updates mid-flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const headerRowHeight = media.gtMd ? 85 : media.gtSm ? 72 : 64;
  const modalTop = isWeb ? 90 : insets.top + headerRowHeight + 24;
  const twoColInstruments = media.gtSm;
  const instrumentOptionRows = useMemo(() => instrumentRows(twoColInstruments), [twoColInstruments]);
  const maxModalHeight = isWeb
    ? undefined
    : screenHeight - modalTop - 48;

  const handleClose = useCallback(() => {
    if (busy) return;
    onOpenChange(false);
  }, [busy, onOpenChange]);

  const handleInstrumentPick = useCallback((id: HomeSurveyInstrumentId) => {
    if (busy) return;
    setInstrument(id);
    setStep("level");
  }, [busy]);

  const handleLevelPick = useCallback(async (level: HomeSurveyLevelId) => {
    if (!instrument || !user || busy) return;
    const levelNumber = level === "beginner" ? 1 : level === "intermediate" ? 2 : 3;
    setBusy(true);
    try {
      const result = await setHomeDashboard({ instrument, level: levelNumber });
      if (result.metadata) {
        applyPreferenceMetadata(result.metadata as UserPreferenceMetadata);
      }
      if (result.supabase) {
        await refreshUserPreferences();
      }
      if (!result.ok) {
        showError(setHomeFailureMessage(result));
        return;
      }
      onOpenChange(false);
      onSuccess?.();
    } finally {
      setBusy(false);
    }
  }, [instrument, user, busy, applyPreferenceMetadata, refreshUserPreferences, showError, onOpenChange, onSuccess]);

  const instrumentLabel =
    HOME_SURVEY_INSTRUMENTS.find((i) => i.id === instrument)?.label ?? "";

  const title =
    step === "instrument"
      ? welcomeHeader?.title ??
        (isUpdateMode ? "Update your dashboard" : "Set your dashboard")
      : "What's your skill level?";

  const subtitle =
    step === "instrument"
      ? welcomeHeader?.subtitle ??
        (isUpdateMode
          ? "Change your primary instrument"
          : "What's your primary instrument?")
      : instrumentLabel
        ? `For ${instrumentLabel}`
        : "Choose the level that fits you best";

  const surveyBody = (
    <YStack f={embedded ? undefined : 1} minHeight={0} w="100%">
      {tone === "onboarding" && embedded ? (
        <XStack ai="center" gap="$2" mb="$3">
          <StepDot active={step === "instrument"} done={step === "level"} label="1" />
          <YStack f={1} height={2} bg="rgba(0,0,0,0.08)" br={1} overflow="hidden">
            <YStack
              width={step === "level" ? "100%" : "0%"}
              height="100%"
              bg={BRAND_COLORS.accent}
              animation="quick"
            />
          </YStack>
          <StepDot active={step === "level"} done={false} label="2" />
        </XStack>
      ) : null}

      <XStack
        ai="center"
        jc="space-between"
        px={embedded ? "$0" : "$4"}
        pt={embedded ? "$0" : "$4"}
        pb="$3"
        borderBottomWidth={embedded ? 0 : 1}
        borderColor="rgba(0,0,0,0.06)"
      >
        <YStack f={1} pr="$3">
          <ContentTitle fontWeight="700" color={BRAND_COLORS.text}>
            {title}
          </ContentTitle>
          <BodyText color={BRAND_COLORS.muted} mt="$1">
            {subtitle}
          </BodyText>
        </YStack>
        {!embedded ? (
          <Pressable onPress={handleClose} hitSlop={12} disabled={busy}>
            <CloseIcon width={32} height={32} color="#666" />
          </Pressable>
        ) : null}
      </XStack>

      {step === "instrument" ? (
        <YStack px={embedded ? 0 : 16} py={12} gap="$2" w="100%">
          {instrumentOptionRows.map((row, rowIndex) => (
            <XStack key={rowIndex} gap="$2" w="100%">
              {row.map((item) => (
                <YStack key={item.id} f={1} flexBasis={0} minWidth={0}>
                  <InstrumentOption
                    item={item}
                    selected={isUpdateMode && prefill.instrument === item.id}
                    onPick={handleInstrumentPick}
                  />
                </YStack>
              ))}
            </XStack>
          ))}
        </YStack>
      ) : (
        <ScrollView
          style={{
            flexGrow: embedded ? 0 : 1,
            flexShrink: embedded ? 0 : 1,
            minHeight: 0,
            maxHeight: embedded
              ? undefined
              : isWeb
                ? undefined
                : maxModalHeight
                  ? maxModalHeight - 160
                  : 520,
          }}
          contentContainerStyle={{ padding: embedded ? 0 : 16, paddingBottom: 16 }}
          keyboardShouldPersistTaps="always"
          nestedScrollEnabled
          showsVerticalScrollIndicator={false}
        >
          <YStack gap="$2">
            {HOME_SURVEY_LEVELS.map((level) => {
              const isCurrent =
                isUpdateMode &&
                instrument === prefill.instrument &&
                prefill.level === level.id;
              return (
                <YStack
                  key={level.id}
                  br={12}
                  borderWidth={1}
                  borderColor={isCurrent ? BRAND_COLORS.text : "rgba(0,0,0,0.08)"}
                  bg={isCurrent ? "#F4F1ED" : BRAND_COLORS.cardBg}
                  px="$4"
                  py="$3"
                  gap="$1"
                  opacity={busy ? 0.7 : 1}
                  hoverStyle={{ borderColor: BRAND_COLORS.text }}
                  pressStyle={{ opacity: 0.92, scale: 0.995 }}
                  onPress={() => handleLevelPick(level.id)}
                  accessibilityRole="button"
                  accessibilityLabel={level.label}
                >
                  <SectionTitle color={BRAND_COLORS.text}>{level.label}</SectionTitle>
                  <BodyText color={BRAND_COLORS.muted}>{level.description}</BodyText>
                </YStack>
              );
            })}
          </YStack>
        </ScrollView>
      )}

      {step === "level" ? (
        <YStack
          height={52}
          px={embedded ? 0 : "$4"}
          borderTopWidth={1}
          borderColor="rgba(0,0,0,0.06)"
          jc="center"
        >
          {busy ? (
            <XStack ai="center" jc="center" gap="$2">
              <ActivityIndicator color={LOADING_SPINNER_COLOR} size="small" />
              <BodyText fontWeight="600" color={BRAND_COLORS.text}>
                {isUpdateMode ? "Updating your dashboard…" : "Setting up your dashboard…"}
              </BodyText>
            </XStack>
          ) : (
            <Button
              size="$3"
              chromeless
              color={BRAND_COLORS.muted}
              fontWeight="600"
              onPress={() => setStep("instrument")}
            >
              ← Back to instruments
            </Button>
          )}
        </YStack>
      ) : embedded && onSkip ? (
        <YStack pt="$4" ai="center" borderTopWidth={tone === "onboarding" ? 1 : 0} borderColor="rgba(0,0,0,0.06)" mt="$2">
          <Button
            size="$3"
            chromeless
            color={BRAND_COLORS.muted}
            fontWeight="600"
            onPress={onSkip}
            disabled={busy}
          >
            {tone === "onboarding" ? "Skip — I'll set this up later" : "Skip for now"}
          </Button>
        </YStack>
      ) : null}
    </YStack>
  );

  if (!open) return null;

  if (embedded) {
    return surveyBody;
  }

  return (
    <Dialog modal open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <Dialog.Portal zIndex={500}>
        <Dialog.Overlay
          key="survey-overlay"
          animation="quick"
          backgroundColor="rgba(0,0,0,0.45)"
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
          onPress={handleClose}
          zIndex={500}
        />
        <Dialog.Content
          key="survey-content"
          animation="quick"
          enterStyle={{ opacity: 0, y: -8 }}
          exitStyle={{ opacity: 0, y: -8 }}
          padding="$0"
          backgroundColor="white"
          borderRadius={12}
          position="absolute"
          top={modalTop}
          alignSelf="center"
          width={isWeb ? "calc(100% - 48px)" : Math.min(560, width - 32)}
          maxWidth={560}
          maxHeight={
            step === "level"
              ? isWeb
                ? "calc(100vh - 130px)"
                : maxModalHeight
              : undefined
          }
          overflow="hidden"
          onPress={(e) => e.stopPropagation()}
          zIndex={501}
        >
          <VisuallyHidden>
            <Dialog.Title>{title}</Dialog.Title>
          </VisuallyHidden>
          {surveyBody}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}

function StepDot({
  active,
  done,
  label,
}: {
  active: boolean;
  done: boolean;
  label: string;
}) {
  const filled = active || done;
  return (
    <YStack
      width={28}
      height={28}
      br={14}
      ai="center"
      jc="center"
      bg={filled ? BRAND_COLORS.accent : "rgba(0,0,0,0.06)"}
      borderWidth={active ? 2 : 0}
      borderColor={BRAND_COLORS.accent}
    >
      {done && !active ? (
        <BodyText color="#FFF" fontWeight="700" fontSize={14}>
          ✓
        </BodyText>
      ) : (
        <CaptionText color={filled ? "#FFF" : BRAND_COLORS.muted} fontWeight="700">
          {label}
        </CaptionText>
      )}
    </YStack>
  );
}
